"use client";

import { useEffect, useState } from "react";
import { ArrowUp } from "@phosphor-icons/react/dist/ssr";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/useMediaQuery";

/**
 * Bouton "retour en haut" flottant. Apparaît au scroll > 600px,
 * respecte prefers-reduced-motion, évite la BottomNav mobile.
 */
export function BackToTop({ threshold = 600 }: { threshold?: number }) {
  const [visible, setVisible] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const t = useTranslations("common");

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > threshold);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  function scrollTop() {
    window.scrollTo({
      top: 0,
      behavior: reducedMotion ? "auto" : "smooth",
    });
  }

  return (
    <button
      type="button"
      onClick={scrollTop}
      aria-label={t("backToTopAria")}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={cn(
        "fixed end-4 z-30 inline-flex h-11 w-11 items-center justify-center rounded-full",
        "bg-[var(--sahha-primary-600)] text-white shadow-[0_10px_30px_-8px_rgba(21,112,239,0.5)]",
        "transition-all duration-200 ease-[var(--ease-out)]",
        "hover:bg-[var(--sahha-primary-700)] hover:shadow-[0_16px_36px_-10px_rgba(21,112,239,0.6)]",
        "focus-visible:outline-3 focus-visible:outline-[var(--sahha-primary-300)] focus-visible:outline-offset-2 active:scale-95",
        // Position verticale : au-dessus de la BottomNav mobile (≈ 4.5rem + safe-area)
        "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:bottom-6",
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-3 opacity-0",
      )}
    >
      <ArrowUp size={18} weight="bold" />
    </button>
  );
}
